import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { authFetch } from "@/lib/authFetch";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, RefreshCw, Map as MapIcon, BarChart2, Building2 } from "lucide-react";
import ResellersCoverage from "@/components/resellers-coverage";

const FRANCE_CENTER: [number, number] = [46.6, 2.5];
const FRANCE_ZOOM = 6;

const CONTRACT_LABELS: Record<string, string> = {
  revendeur: "Revendeur",
  revendeur_premium: "Revendeur Premium",
  sous_distributeur: "Sous-distributeur",
  apporteur_affaires: "Apporteur d'affaires",
  annule: "Annulé",
};

const SITE_TYPE_LABELS: Record<string, string> = {
  point_de_vente: "Point de vente",
  depot: "Dépôt",
  sav: "SAV",
  siege: "Siège",
  autre: "Autre",
};

function logoIcon(logoUrl: string | null | undefined, label: string) {
  const initial = (label || "?")[0].toUpperCase();
  const inner = logoUrl
    ? `<img src="${logoUrl}" alt="" style="width:32px;height:32px;border-radius:6px;object-fit:contain;background:#fff" />`
    : `<div style="width:32px;height:32px;border-radius:6px;background:#2563eb;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px">${initial}</div>`;
  return L.divIcon({
    html: `<div style="padding:3px;background:#fff;border:2px solid #2563eb;border-radius:9px;box-shadow:0 2px 8px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center">${inner}</div>`,
    className: "",
    iconSize: [44, 44],
    iconAnchor: [22, 44],
    popupAnchor: [0, -46],
  });
}

function siteIcon(typeSite: string) {
  const colors: Record<string, string> = {
    point_de_vente: "#2563eb",
    depot: "#7c3aed",
    sav: "#dc2626",
    siege: "#059669",
    autre: "#64748b",
  };
  const color = colors[typeSite] ?? "#64748b";
  return L.divIcon({
    html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
    className: "",
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -12],
  });
}

interface MapReseller {
  id: string;
  companyName: string;
  nomCommercial?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contractStatus: string;
  actif: boolean;
  geographicZones?: string | null;
  logoUrl?: string | null;
  latitude: number;
  longitude: number;
  geocodingStatus?: string | null;
}

interface MapSite {
  id: string;
  nom: string;
  typeSite: string;
  livraisonAdresse?: string | null;
  livraisonCodePostal?: string | null;
  livraisonVille?: string | null;
  latitude: number;
  longitude: number;
  geocodeStatut: string;
  estSiteParDefaut: boolean;
  revendeurId: string;
  companyName: string;
  logoUrl?: string | null;
  contractStatus: string;
}

interface MapData {
  located: MapReseller[];
  unlocated_count: number;
  unlocated: any[];
}

interface RevendeursMapProps {
  onOpenReseller?: (id: string) => void;
}

export default function RevendeursMap({ onOpenReseller }: RevendeursMapProps = {}) {
  const [mapMode, setMapMode] = useState<"emplacements" | "couverture" | "sites">("emplacements");
  const [data, setData] = useState<MapData | null>(null);
  const [sitesData, setSitesData] = useState<MapSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await authFetch("/api/resellers/map");
      const d = await r.json();
      setData(d);
    } catch {
      setData({ located: [], unlocated_count: 0, unlocated: [] });
    }
    setLoading(false);
  }

  async function loadSites() {
    setLoading(true);
    try {
      const r = await authFetch("/api/revendeur-sites/carte");
      const d = await r.json();
      setSitesData(Array.isArray(d) ? d : []);
    } catch {
      setSitesData([]);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (mapMode === "sites") {
      loadSites();
    } else if (mapMode === "emplacements") {
      load();
    }
  }, [mapMode]);

  useEffect(() => { load(); }, []);

  async function runGeocoding() {
    setGeocoding(true);
    try {
      await authFetch("/api/resellers/geocode-all", { method: "POST" });
    } catch {}
    setGeocoding(false);
    await load();
  }

  async function runSitesGeocoding() {
    setGeocoding(true);
    try {
      await authFetch("/api/revendeur-sites/geocoder-en-masse", { method: "POST" });
    } catch {}
    setGeocoding(false);
    await loadSites();
  }

  const modeToggle = (
    <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-lg w-fit border mb-3">
      <Button
        variant={mapMode === "emplacements" ? "default" : "ghost"}
        size="sm"
        className="rounded-md h-7 text-xs"
        onClick={() => setMapMode("emplacements")}
        data-testid="mapmode-emplacements"
      >
        <MapIcon className="w-3.5 h-3.5 mr-1.5" />
        Emplacements
      </Button>
      <Button
        variant={mapMode === "couverture" ? "default" : "ghost"}
        size="sm"
        className="rounded-md h-7 text-xs"
        onClick={() => setMapMode("couverture")}
        data-testid="mapmode-couverture"
      >
        <BarChart2 className="w-3.5 h-3.5 mr-1.5" />
        Couverture
      </Button>
      <Button
        variant={mapMode === "sites" ? "default" : "ghost"}
        size="sm"
        className="rounded-md h-7 text-xs"
        onClick={() => setMapMode("sites")}
        data-testid="mapmode-sites"
      >
        <Building2 className="w-3.5 h-3.5 mr-1.5" />
        Sites
      </Button>
    </div>
  );

  if (mapMode === "couverture") {
    return (
      <div>
        {modeToggle}
        <ResellersCoverage onOpenReseller={onOpenReseller} />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        {modeToggle}
        <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Chargement de la carte…
        </div>
      </div>
    );
  }

  if (mapMode === "sites") {
    const ungeocoded = sitesData.filter((s) => s.geocodeStatut !== "ok").length;
    return (
      <div className="space-y-3">
        {modeToggle}
        {ungeocoded > 0 && (
          <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-300 text-sm">
            <MapPin className="w-4 h-4 shrink-0" />
            <span>
              <strong>{ungeocoded}</strong> site{ungeocoded > 1 ? "s" : ""} sans coordonnées.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="ml-auto shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900"
              onClick={runSitesGeocoding}
              disabled={geocoding}
              data-testid="button-geocode-sites"
            >
              {geocoding ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Géocodage…</>
              ) : (
                <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Géocoder</>
              )}
            </Button>
          </div>
        )}
        {sitesData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground gap-3">
            <Building2 className="w-8 h-8 opacity-30" />
            <p className="text-sm">Aucun site géolocalisé pour l'instant.</p>
          </div>
        ) : (
          <div style={{ height: "72vh", width: "100%", borderRadius: "12px", overflow: "hidden" }}>
            <MapContainer
              center={FRANCE_CENTER}
              zoom={FRANCE_ZOOM}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MarkerClusterGroup chunkedLoading>
                {sitesData.filter((s) => s.latitude && s.longitude).map((s) => (
                  <Marker
                    key={s.id}
                    position={[s.latitude, s.longitude]}
                    icon={siteIcon(s.typeSite)}
                  >
                    <Popup minWidth={220}>
                      <div className="space-y-1 text-sm py-1">
                        <div className="font-semibold text-base leading-tight">{s.nom}</div>
                        <div className="text-gray-500 text-xs">{s.companyName}</div>
                        <div className="text-xs">
                          <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                            {SITE_TYPE_LABELS[s.typeSite] ?? s.typeSite}
                          </span>
                          {s.estSiteParDefaut && (
                            <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium text-[10px]">
                              Défaut
                            </span>
                          )}
                        </div>
                        {(s.livraisonVille || s.livraisonCodePostal) && (
                          <div className="text-xs text-gray-500">
                            {s.livraisonCodePostal} {s.livraisonVille}
                          </div>
                        )}
                        {onOpenReseller && (
                          <div className="pt-1">
                            <button
                              type="button"
                              onClick={() => onOpenReseller(s.revendeurId)}
                              style={{ background: "none", border: 0, color: "#2563eb", cursor: "pointer", padding: 0, font: "inherit", textDecoration: "underline", fontSize: "12px" }}
                            >
                              Ouvrir la fiche →
                            </button>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
        )}
      </div>
    );
  }

  const displayName = (r: MapReseller) => r.nomCommercial || r.companyName;
  const contactName = (r: MapReseller) =>
    [r.contactFirstName, r.contactLastName].filter(Boolean).join(" ") || null;

  return (
    <div className="space-y-3">
      {modeToggle}
      {(data?.unlocated_count ?? 0) > 0 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-300 text-sm">
          <MapPin className="w-4 h-4 shrink-0" />
          <span>
            <strong>{data!.unlocated_count}</strong> revendeur{data!.unlocated_count > 1 ? "s" : ""} sans coordonnées (adresse manquante ou non reconnue).
          </span>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900"
            onClick={runGeocoding}
            disabled={geocoding}
            data-testid="button-geocode-all"
          >
            {geocoding ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Géocodage…</>
            ) : (
              <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Géocoder maintenant</>
            )}
          </Button>
        </div>
      )}

      {(data?.located.length ?? 0) === 0 && !geocoding ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground gap-3">
          <MapPin className="w-8 h-8 opacity-30" />
          <p className="text-sm">Aucun revendeur géolocalisé pour l'instant.</p>
          {(data?.unlocated_count ?? 0) > 0 && (
            <Button size="sm" onClick={runGeocoding} disabled={geocoding} data-testid="button-geocode-empty">
              Lancer le géocodage
            </Button>
          )}
        </div>
      ) : (
        <div style={{ height: "72vh", width: "100%", borderRadius: "12px", overflow: "hidden" }}>
          <MapContainer
            center={FRANCE_CENTER}
            zoom={FRANCE_ZOOM}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MarkerClusterGroup chunkedLoading>
              {(data?.located ?? []).map((r) => (
                <Marker
                  key={r.id}
                  position={[r.latitude, r.longitude]}
                  icon={logoIcon(r.logoUrl, displayName(r))}
                >
                  <Popup minWidth={220}>
                    <div className="space-y-1 text-sm py-1">
                      <div className="font-semibold text-base leading-tight">{displayName(r)}</div>
                      {contactName(r) && <div className="text-gray-600">{contactName(r)}</div>}
                      {r.contactEmail && <div className="text-gray-500 text-xs">{r.contactEmail}</div>}
                      {r.contactPhone && <div className="text-gray-500 text-xs">{r.contactPhone}</div>}
                      <div className="text-xs mt-1 flex items-center gap-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-medium ${r.actif ? "bg-green-600" : "bg-gray-400"}`}>
                          {r.actif ? "Actif" : "Inactif"}
                        </span>
                        <span className="text-gray-500">{CONTRACT_LABELS[r.contractStatus] ?? r.contractStatus}</span>
                      </div>
                      {r.geographicZones && (
                        <div className="text-xs text-gray-400">Zones : {r.geographicZones}</div>
                      )}
                      {onOpenReseller && (
                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => onOpenReseller(r.id)}
                            style={{ background: "none", border: 0, color: "#2563eb", cursor: "pointer", padding: 0, font: "inherit", textDecoration: "underline", fontSize: "12px" }}
                          >
                            Ouvrir la fiche →
                          </button>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </div>
      )}
    </div>
  );
}
